import * as yup from 'yup';
import { createProcessSchema } from './schema.js';

/** Fields validated with the manual create-process Yup schema. */
export type CoreDraftField = 'name' | 'description' | 'owner' | 'actors' | 'systems';

/** All editable columns in the draft review table (includes bulk-upload SOX cycle). */
export type ProcessDraftTableField = 'soxCycle' | CoreDraftField;

/** @deprecated Prefer `ProcessDraftTableField` — kept for existing imports. */
export type DraftField = ProcessDraftTableField;

export type ProcessDraftValidationRow = {
  id: string;
  /** SOX cycle code from upload file (e.g. `otc`), when present in `file_data`. */
  soxCycle?: string | null;
  name?: string | null;
  description?: string | null;
  owner?: string | null;
  actors?: string[] | string | null;
  systems?: string[] | string | null;
  file_ids?: string[] | null;
  sourceDocuments?: Array<{
    file_id?: string;
    file_name?: string;
    tags?: string[];
  }>;
  validation_errors?: Array<{ column: string; reason?: string; message?: string }>;
};

function errorText(e: { reason?: string; message?: string }): string {
  return String(e.reason ?? e.message ?? '').trim();
}

export function normalizeColumnLabel(column: string): string {
  return String(column || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function columnMapsToField(column: string, field: ProcessDraftTableField): boolean {
  const n = normalizeColumnLabel(column);
  switch (field) {
    case 'soxCycle':
      return (
        n === 'soxcycle' ||
        n === 'cycle' ||
        n === 'soxcycleid' ||
        n === 'cycleid' ||
        n === 'cyclecode' ||
        (n.includes('sox') && n.includes('cycle'))
      );
    case 'name':
      return (
        n === 'name' ||
        n === 'processname' ||
        n === 'processtitle' ||
        n === 'title' ||
        (n.includes('process') && (n.includes('name') || n.includes('title')))
      );
    case 'description':
      return n.includes('description') || n === 'desc' || n === 'processdescription';
    case 'owner':
      return n.includes('owner') || n === 'processowner';
    case 'actors':
      return n.includes('actor');
    case 'systems':
      return n.includes('system');
    default:
      return false;
  }
}

export function reasonsForBackendField(
  errors: ProcessDraftValidationRow['validation_errors'],
  field: ProcessDraftTableField
): string[] {
  if (!errors?.length) return [];
  return errors
    .filter((e) => columnMapsToField(String(e.column), field))
    .map((e) => errorText(e))
    .filter(Boolean);
}

/** Max length for SOX cycle in bulk / AI draft cells (matches route or spreadsheet codes longer than 3 chars). */
export const PROCESS_DRAFT_SOX_CYCLE_MAX_LEN = 50;

/** Normalize SOX cycle for comparison with route `cycleId` (trim, uppercase, length cap). */
export function normalizeSoxCycleCode(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .slice(0, PROCESS_DRAFT_SOX_CYCLE_MAX_LEN);
}

export type DraftRowMergeOptions = {
  showBackendErrors: boolean;
  applyFrontendValidation: boolean;
  /** When false, SOX cycle is omitted from row validity (e.g. AI-assisted draft table). Default true. */
  includeSoxCycle?: boolean;
  /**
   * Current SOX cycle from the app (e.g. route `cycleId`). When set:
   * - While the row's SOX value does not match and there are SOX-column backend errors, only those errors are shown (other columns wait).
   * - When the row's SOX value matches, SOX-column backend errors are treated as resolved and omitted from merge.
   */
  expectedSoxCycle?: string | null;
};

function looksLikeRequiredOrMissingReason(reason: string): boolean {
  const r = reason.toLowerCase();
  return (
    r.includes('required') ||
    r.includes('missing') ||
    r.includes('empty') ||
    r.includes('blank') ||
    r.includes('not provided')
  );
}

/**
 * Core fields whose length/required rules match the manual create form (Yup).
 * Bulk upload validate API snapshots errors on upload; after SOX cycle is fixed those
 * stale backend messages (e.g. old max 2000) must not override live Yup validation.
 */
const FRONTEND_AUTHORED_CORE_FIELDS: CoreDraftField[] = ['name', 'description', 'owner'];

/**
 * True when a backend cell error should not be shown because the user has already corrected that cell
 * (original errors stay on `row.validation_errors` for regression if they change the value back).
 */
function isBackendCellErrorSupersededByCurrentValues(
  err: { column: string; reason?: string; message?: string },
  row: ProcessDraftValidationRow,
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown,
  opts: DraftRowMergeOptions
): boolean {
  const reason = errorText(err);
  if (!reason) return false;
  const col = String(err.column || '');

  if (columnMapsToField(col, 'soxCycle')) {
    const exp = normalizeSoxCycleCode(opts.expectedSoxCycle);
    if (!exp) return false;
    return normalizeSoxCycleCode(getFieldValue(row, 'soxCycle')) === exp;
  }

  const coreField = (['name', 'description', 'owner', 'actors', 'systems'] as const).find((f) =>
    columnMapsToField(col, f)
  );
  if (coreField && FRONTEND_AUTHORED_CORE_FIELDS.includes(coreField)) {
    const frontendErrors = validateDraftRowFrontendSync(row, getFieldValue)[coreField];
    return !frontendErrors?.length;
  }

  if (!looksLikeRequiredOrMissingReason(reason)) return false;

  if (columnMapsToField(col, 'name')) {
    return String(getFieldValue(row, 'name') ?? '').trim().length > 0;
  }
  if (columnMapsToField(col, 'description')) {
    return String(getFieldValue(row, 'description') ?? '').trim().length > 0;
  }
  if (columnMapsToField(col, 'owner')) {
    return String(getFieldValue(row, 'owner') ?? '').trim().length > 0;
  }
  if (columnMapsToField(col, 'actors')) {
    const v = getFieldValue(row, 'actors');
    if (Array.isArray(v)) return v.length > 0;
    return String(v ?? '').trim().length > 0;
  }
  if (columnMapsToField(col, 'systems')) {
    const v = getFieldValue(row, 'systems');
    if (Array.isArray(v)) return v.length > 0;
    return String(v ?? '').trim().length > 0;
  }
  return false;
}

/**
 * Backend `validation_errors` after applying SOX-vs-route rules so the UI matches server intent.
 */
export function effectiveBackendErrorsForRow(
  row: ProcessDraftValidationRow,
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown,
  opts: DraftRowMergeOptions
): NonNullable<ProcessDraftValidationRow['validation_errors']> {
  const raw = Array.isArray(row.validation_errors) ? row.validation_errors : [];
  if (!opts.showBackendErrors) return [];

  const expected = normalizeSoxCycleCode(opts.expectedSoxCycle);

  let working = raw;
  if (expected) {
    const current = normalizeSoxCycleCode(getFieldValue(row, 'soxCycle'));
    const hasSoxBackendError = raw.some((e) => columnMapsToField(String(e.column), 'soxCycle'));

    if (hasSoxBackendError && current !== expected) {
      working = raw.filter((e) => columnMapsToField(String(e.column), 'soxCycle'));
    } else if (current === expected) {
      working = raw.filter((e) => !columnMapsToField(String(e.column), 'soxCycle'));
    }
  }

  return working.filter((e) => !isBackendCellErrorSupersededByCurrentValues(e, row, getFieldValue, opts));
}

export function orphanBackendReasons(
  errors: ProcessDraftValidationRow['validation_errors']
): string[] {
  if (!errors?.length) return [];
  const fields: ProcessDraftTableField[] = ['soxCycle', 'name', 'description', 'owner', 'actors', 'systems'];
  return errors
    .filter((e) => !fields.some((f) => columnMapsToField(String(e.column), f)))
    .map((e) => {
      const r = errorText(e);
      const c = String(e.column || '').trim();
      if (c && r) return `${c}: ${r}`;
      return r || c;
    })
    .filter(Boolean);
}

/** Remove backend validation entries that apply to a specific draft field (after user edits that field). */
export function stripBackendErrorsForMappedField(
  errors: ProcessDraftValidationRow['validation_errors'] | undefined,
  field: ProcessDraftTableField
): Array<{ column: string; reason?: string; message?: string }> {
  if (!errors?.length) return [];
  return errors.filter((e) => !columnMapsToField(String(e.column), field));
}

function actorsSystemsToComma(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((x) => String(x ?? '').trim()).filter(Boolean).join(', ');
  }
  return String(value ?? '').trim();
}

/** Shape expected by `createProcessSchema` (manual create form). */
export function rowToCreateProcessShape(
  row: ProcessDraftValidationRow,
  getFieldValue: (row: ProcessDraftValidationRow, field: CoreDraftField) => unknown
): {
  name: string;
  description: string;
  owner: string;
  actors: string;
  systems: string;
} {
  return {
    name: String(getFieldValue(row, 'name') ?? '').trim(),
    description: String(getFieldValue(row, 'description') ?? '').trim(),
    owner: String(getFieldValue(row, 'owner') ?? '').trim(),
    actors: actorsSystemsToComma(getFieldValue(row, 'actors')),
    systems: actorsSystemsToComma(getFieldValue(row, 'systems'))
  };
}

export type FrontendFieldErrors = Partial<Record<CoreDraftField, string[]>>;

/** Run the same Yup rules as the manual “Create process” form. */
export function validateDraftRowFrontendSync(
  row: ProcessDraftValidationRow,
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown
): FrontendFieldErrors {
  const shape = rowToCreateProcessShape(row, (r, f) => getFieldValue(r, f));
  const out: FrontendFieldErrors = {};
  try {
    createProcessSchema.validateSync(shape, { abortEarly: false });
  } catch (e: unknown) {
    if (e instanceof yup.ValidationError) {
      const inner = e.inner && e.inner.length > 0 ? e.inner : [e];
      for (const err of inner) {
        const path = err.path as CoreDraftField | undefined;
        if (!path) continue;
        if (out[path]?.length) continue;
        out[path] = [String(err.message || 'Invalid value')];
      }
    }
  }
  return out;
}

export function mergedFieldReasons(
  row: ProcessDraftValidationRow,
  field: ProcessDraftTableField,
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown,
  options: DraftRowMergeOptions
): string[] {
  const backendList = effectiveBackendErrorsForRow(row, getFieldValue, options);

  if (field === 'soxCycle') {
    return options.showBackendErrors ? reasonsForBackendField(backendList, 'soxCycle') : [];
  }

  const coreField = field as CoreDraftField;

  if (
    options.applyFrontendValidation &&
    FRONTEND_AUTHORED_CORE_FIELDS.includes(coreField)
  ) {
    return validateDraftRowFrontendSync(row, getFieldValue)[coreField] || [];
  }

  const backendField = options.showBackendErrors ? reasonsForBackendField(backendList, coreField) : [];
  const orphans =
    coreField === 'name' && options.showBackendErrors ? orphanBackendReasons(backendList) : [];
  /** Prefer API cell errors; avoid stacking the same Yup message when the backend already reported this field. */
  const backendCoversCoreField =
    backendField.length > 0 || (coreField === 'name' && orphans.length > 0);
  const frontend =
    options.applyFrontendValidation && !backendCoversCoreField
      ? validateDraftRowFrontendSync(row, getFieldValue)[coreField] || []
      : [];

  if (coreField === 'name') {
    return [...backendField, ...orphans, ...frontend];
  }
  return [...backendField, ...frontend];
}

const ALL_DRAFT_FIELDS: ProcessDraftTableField[] = [
  'soxCycle',
  'name',
  'description',
  'owner',
  'actors',
  'systems',
];

export function draftFieldsForMergeOptions(options: DraftRowMergeOptions): ProcessDraftTableField[] {
  return options.includeSoxCycle === false
    ? ALL_DRAFT_FIELDS.filter((f) => f !== 'soxCycle')
    : ALL_DRAFT_FIELDS;
}

export function draftRowHasAnyMergedError(
  row: ProcessDraftValidationRow,
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown,
  options: DraftRowMergeOptions
): boolean {
  return draftFieldsForMergeOptions(options).some(
    (f) => mergedFieldReasons(row, f, getFieldValue, options).length > 0
  );
}

export function defaultSelectedProcessDraftIds(
  rows: ProcessDraftValidationRow[],
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown,
  options: DraftRowMergeOptions
): Set<string> {
  return new Set(
    rows.filter((row) => !draftRowHasAnyMergedError(row, getFieldValue, options)).map((row) => row.id)
  );
}

export function selectedDraftRowsHaveMergedErrors(
  rows: ProcessDraftValidationRow[],
  selectedIds: Set<string>,
  getFieldValue: (row: ProcessDraftValidationRow, field: ProcessDraftTableField) => unknown,
  options: DraftRowMergeOptions
): boolean {
  return rows.some(
    (row) => selectedIds.has(row.id) && draftRowHasAnyMergedError(row, getFieldValue, options)
  );
}
